import { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import Select from "react-select";
import { server } from "../../server";
import { fetchStudents } from "../../redux/studentSlice";
import { formatDate } from "../../functions";

// ─── helpers ──────────────────────────────────────────────────────────────────
const toUpper = (v) =>
  v !== null && v !== undefined ? String(v).toUpperCase() : "";

const today = new Date().toISOString().split("T")[0];

const writeWorkbook = (wb, filename) => {
  const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  saveAs(new Blob([buf], { type: "application/octet-stream" }), filename);
  toast.success("Report Generated");
};

const fmtDate = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  return isNaN(dt) ? "" : dt.toLocaleDateString("en-GB");
};

const getValueByPath = (obj, path) =>
  path.split(".").reduce((acc, key) => {
    if (!acc) return "";
    const match = key.match(/(.*?)\[(\d+)\]/);
    if (match) {
      const arrayKey = match[1];
      const idx = parseInt(match[2], 10);
      return acc[arrayKey]?.[idx] !== undefined ? acc[arrayKey][idx] : "";
    }
    return acc[key] !== undefined ? acc[key] : "";
  }, obj);

// ─── dataset meta ─────────────────────────────────────────────────────────────
const DATASETS = {
  buses: { label: "Active Buses", color: "success" },
  drivers: { label: "Drivers", color: "info" },
  students: { label: "Students", color: "warning" },
  leftbuses: { label: "Left Buses", color: "danger" },
  busatt: { label: "Bus Attendance", color: "success" },
  busattstat: { label: "Att. Statistics", color: "info" },
};

// ─── report categories ────────────────────────────────────────────────────────
const REPORT_CATEGORIES = [
  { key: "all", label: "All Reports" },
  { key: "buses", label: "Buses" },
  { key: "drivers", label: "Drivers" },
  { key: "students", label: "Students" },
  { key: "routes", label: "Routes" },
  { key: "busatt", label: "Bus Attendance" },
  { key: "analytics", label: "Analytics" },
];

// ─────────────────────────────────────────────────────────────────────────────
function TransportReports() {
  const dispatch = useDispatch();
  const { studentDetails, loading: loadingStudents } = useSelector(
    (s) => s.students,
  );

  // ── local data ─────────────────────────────────────────────────────────────
  const [busData, setBusData] = useState([]);
  const [driverData, setDriverData] = useState([]);
  const [leftBusData, setLeftBusData] = useState([]);
  const [leftDriverData, setLeftDriverData] = useState([]);
  const [routeData, setRouteData] = useState([]);
  const [stoppageData, setStoppageData] = useState([]);
  const [timingData, setTimingData] = useState([]);

  const [loadingBuses, setLoadingBuses] = useState(false);
  const [loadingDrivers, setLoadingDrivers] = useState(false);
  const [loadingLeft, setLoadingLeft] = useState(false);
  const [loadingRoutes, setLoadingRoutes] = useState(false);

  // ── bus attendance state ───────────────────────────────────────────────────
  const [busAttData, setBusAttData] = useState([]); // all bus pickup+drop (BusAttendance)
  const [busAttStatData, setBusAttStatData] = useState([]); // bus-wise summary stats (BusAttendanceStatistics)
  const [loadingBusAtt, setLoadingBusAtt] = useState(false);
  const [loadingBusAttSt, setLoadingBusAttSt] = useState(false);

  // ── UI state ───────────────────────────────────────────────────────────────
  const [activeDataset, setActiveDataset] = useState("buses");
  const [activeCategory, setActiveCategory] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedBusNo, setSelectedBusNo] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // ── fetches ────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (studentDetails.length === 0) dispatch(fetchStudents());

    // Active buses
    setLoadingBuses(true);
    fetch(`${server}/allBusDetailsMongoDb`, {
      headers: { Authorization: localStorage.getItem("token") },
    })
      .then((r) => r.json())
      .then((d) => setBusData(d?.["body-json"]?.body?.data || []))
      .catch(console.error)
      .finally(() => setLoadingBuses(false));

    // Drivers
    setLoadingDrivers(true);
    fetch(`${server}/getAllDriversMongoDb`, {
      headers: { Authorization: localStorage.getItem("token") },
    })
      .then((r) => r.json())
      .then((d) => setDriverData(d?.["body-json"]?.body?.drivers || []))
      .catch(console.error)
      .finally(() => setLoadingDrivers(false));

    // Left buses
    setLoadingLeft(true);
    fetch(
      "https://q1xgsyocc2.execute-api.ap-south-1.amazonaws.com/Prod/LeftBus",
      {
        headers: { Authorization: localStorage.getItem("token") },
      },
    )
      .then((r) => r.json())
      .then((d) =>
        setLeftBusData(
          Array.isArray(d?.body?.data)
            ? d.body.data
            : Array.isArray(d?.data)
              ? d.data
              : [],
        ),
      )
      .catch(console.error)
      .finally(() => setLoadingLeft(false));

    // Left drivers
    fetch(
      "https://q1xgsyocc2.execute-api.ap-south-1.amazonaws.com/Prod/LeftDrivers",
      {
        headers: { Authorization: localStorage.getItem("token") },
      },
    )
      .then((r) => r.json())
      .then((d) =>
        setLeftDriverData(
          Array.isArray(d?.body?.data)
            ? d.body.data
            : Array.isArray(d?.data)
              ? d.data
              : [],
        ),
      )
      .catch(console.error);

    // Routes
    setLoadingRoutes(true);
    fetch(`${server}/getAllBusRoutesMongoDb`, {
      headers: { Authorization: localStorage.getItem("token") },
    })
      .then((r) => r.json())
      .then((d) => setRouteData(d?.["body-json"]?.body?.data || []))
      .catch(console.error)
      .finally(() => setLoadingRoutes(false));

    // Stoppages
    fetch(`${server}/getAllBusStoppageMongoDb`, {
      headers: { Authorization: localStorage.getItem("token") },
    })
      .then((r) => r.json())
      .then((d) => setStoppageData(d?.["body-json"]?.body?.data || []))
      .catch(console.error);

    // Timings
    fetch(`${server}/getBusTiming_mongodb`, {
      headers: { Authorization: localStorage.getItem("token") },
    })
      .then((r) => r.json())
      .then((d) => setTimingData(d?.["body-json"]?.body?.data || []))
      .catch(console.error);

    // Bus Attendance (all pickup + drop records for today)
    setLoadingBusAtt(true);
    fetch(`${server}/school_attendance_of_buspickup_and_busdrop_mongodb`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: localStorage.getItem("token"),
      },
      body: JSON.stringify({ date: today }),
    })
      .then((r) => r.json())
      .then((d) => setBusAttData(d?.["body-json"]?.body?.attendance || []))
      .catch(console.error)
      .finally(() => setLoadingBusAtt(false));

    // Bus Attendance Statistics (bus-wise summary: PickupPresent, PickupAbsent, DropPresent, DropAbsent + marked status)
    setLoadingBusAttSt(true);
    Promise.all([
      fetch(`${server}/busAttendance_status_datewise_mongodb`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: localStorage.getItem("token"),
        },
        body: JSON.stringify({ date: today }),
      }).then((r) => r.json()),
      fetch(`${server}/dailyAttendance_allRecords_dateWise_typeWise`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ date: today, type: "bus" }),
      }).then((r) => r.json()),
    ])
      .then(([summaryJson, markedJson]) => {
        const summaryData = JSON.parse(
          summaryJson?.["body-json"]?.body || "[]",
        );
        const markedData = markedJson?.["body-json"]?.body?.data || [];
        const pickupMarked = new Set();
        const dropMarked = new Set();
        markedData.forEach((e) => {
          const type = e.attendance?.type;
          const busNo = e.attendance?.busNo?.toString();
          if (type === "busPickup") pickupMarked.add(busNo);
          if (type === "busDrop") dropMarked.add(busNo);
        });
        setBusAttStatData(
          summaryData.map((bus) => ({
            ...bus,
            isPickupMarked: pickupMarked.has(bus.BusNo?.toString())
              ? "Marked"
              : "Not Marked",
            isDropMarked: dropMarked.has(bus.BusNo?.toString())
              ? "Marked"
              : "Not Marked",
          })),
        );
      })
      .catch(console.error)
      .finally(() => setLoadingBusAttSt(false));
  }, [dispatch]);

  // ── transport students (availing bus) ─────────────────────────────────────
  const transportStudents = useMemo(
    () => studentDetails.filter((s) => s.transportFacility?.busStop),
    [studentDetails],
  );
  const walkerStudents = useMemo(
    () => studentDetails.filter((s) => !s.transportFacility?.busStop),
    [studentDetails],
  );

  // ── dataset map ────────────────────────────────────────────────────────────
  const datasetMap = {
    buses: { data: busData, loading: loadingBuses },
    drivers: { data: driverData, loading: loadingDrivers },
    students: { data: transportStudents, loading: loadingStudents },
    leftbuses: { data: leftBusData, loading: loadingLeft },
    busatt: { data: busAttData, loading: loadingBusAtt },
    busattstat: { data: busAttStatData, loading: loadingBusAttSt },
  };

  const currentRaw = datasetMap[activeDataset]?.data || [];
  const currentLoading = datasetMap[activeDataset]?.loading || false;

  // ── bus number options (dynamic) ──────────────────────────────────────────
  const busNoOptions = useMemo(() => {
    const set = new Set(
      busData.map((b) => b.generalInfo?.busNumber).filter(Boolean),
    );
    return Array.from(set)
      .sort((a, b) => Number(a) - Number(b))
      .map((n) => ({ value: n, label: `Bus ${n}` }));
  }, [busData]);

  // ── filtered data ──────────────────────────────────────────────────────────
  const filteredData = useMemo(() => {
    let data = [...currentRaw];

    if (selectedBusNo) {
      data = data.filter((r) => {
        const busNo =
          r.generalInfo?.busNumber ||
          r.busNumber ||
          r.transportFacility?.busNumber ||
          "";
        return String(busNo) === String(selectedBusNo);
      });
    }

    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      const flatten = (obj) => {
        const vals = [];
        for (const k in obj) {
          const v = obj[k];
          if (v && typeof v === "object") vals.push(...flatten(v));
          else if (typeof v === "string") vals.push(v);
        }
        return vals;
      };
      data = data.filter((r) =>
        flatten(r).some((v) => v.toLowerCase().includes(lower)),
      );
    }

    return data;
  }, [currentRaw, selectedBusNo, searchTerm]);

  // ── live snapshot ──────────────────────────────────────────────────────────
  const snapshot = useMemo(() => {
    const totalBuses = busData.length;
    const totalSeats = busData.reduce(
      (s, b) => s + Number(b.generalInfo?.numberOfSeats || 0),
      0,
    );
    const withCCTV = busData.filter(
      (b) => (b.generalInfo?.cctvInstalled || "").toLowerCase() === "yes",
    ).length;
    const withGPRS = busData.filter(
      (b) => (b.generalInfo?.gprsStatus || "").toLowerCase() === "active",
    ).length;
    const totalDrivers = driverData.length;
    const totalTransport = transportStudents.length;
    const totalWalkers = walkerStudents.length;
    const leftBuses = leftBusData.length;

    // Bus attendance today
    const busPickupPresent = busAttData.filter(
      (s) => s?.busPickup?.status === "Present",
    ).length;
    const busPickupAbsent = busAttData.filter(
      (s) => s?.busPickup?.status === "Absent",
    ).length;
    const busDropPresent = busAttData.filter(
      (s) => s?.busDrop?.status === "Present",
    ).length;
    const notMarkedBuses = busAttStatData.filter(
      (b) =>
        b.isPickupMarked === "Not Marked" && b.isDropMarked === "Not Marked",
    ).length;

    return {
      totalBuses,
      totalSeats,
      withCCTV,
      withGPRS,
      totalDrivers,
      totalTransport,
      totalWalkers,
      leftBuses,
      busPickupPresent,
      busPickupAbsent,
      busDropPresent,
      notMarkedBuses,
    };
  }, [
    busData,
    driverData,
    transportStudents,
    walkerStudents,
    leftBusData,
    busAttData,
    busAttStatData,
  ]);

  // ── select styles ──────────────────────────────────────────────────────────
  const selectStyles = {
    control: (b) => ({
      ...b,
      border: "none",
      boxShadow: "none",
      backgroundColor: "transparent",
      minHeight: "32px",
      cursor: "pointer",
    }),
    valueContainer: (b) => ({ ...b, padding: "0" }),
    indicatorSeparator: () => ({ display: "none" }),
    dropdownIndicator: (b) => ({ ...b, padding: "4px" }),
    menu: (b) => ({
      ...b,
      borderRadius: "12px",
      overflow: "hidden",
      boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
      border: "none",
      zIndex: 5,
    }),
    option: (b, s) => ({
      ...b,
      backgroundColor: s.isSelected
        ? "#ffc107"
        : s.isFocused
          ? "#f8f9fa"
          : "white",
      color: s.isSelected ? "white" : "#212529",
      cursor: "pointer",
      fontSize: "14px",
    }),
    placeholder: (b) => ({ ...b, color: "#6c757d", fontSize: "14px" }),
    singleValue: (b) => ({ ...b, color: "#212529", fontSize: "14px" }),
  };

  // ─────────────────────────────────────────────────────────────────────────
  //  ROW FORMATTERS
  // ─────────────────────────────────────────────────────────────────────────
  const formatBusRow = (b) => {
    const g = b.generalInfo || {};
    const ins = b.insurance || {};
    return {
      "BUS NO": toUpper(g.busNumber || ""),
      "VEHICLE NO": toUpper(g.vehicleNumber || ""),
      "OWNER NAME": toUpper(g.ownerName || ""),
      "CHASSIS NO": toUpper(g.chassisNumber || ""),
      "ENGINE NO": toUpper(g.engineNumber || ""),
      "TOTAL SEATS": String(g.numberOfSeats || ""),
      "GPRS STATUS": toUpper(g.gprsStatus || ""),
      "CCTV INSTALLED": toUpper(g.cctvInstalled || ""),
      "TAX FROM": fmtDate(g.tasPaidDate),
      "TAX TO": fmtDate(g.tasPaidExpiryDate),
      "FITNESS FROM": fmtDate(g.fitenessDate),
      "FITNESS TO": fmtDate(g.fitnessRenewalDate),
      "POLLUTION FROM": fmtDate(g.pollutionControlDate),
      "POLLUTION TO": fmtDate(g.pollutionControlExpiryDate),
      "INSURANCE COMPANY": toUpper(ins.insuranceCompany || ""),
      "POLICY NO": toUpper(ins.insurancePolicyNumber || ""),
      "INSURANCE AMOUNT": String(ins.insuranceAmount || ""),
      "INSURANCE FROM": fmtDate(ins.insuranceDate),
      "INSURANCE TO": fmtDate(ins.insuranceExpiryDate),
    };
  };

  const formatDriverRow = (d) => ({
    "DRIVER ID": toUpper(d.driverId || ""),
    "DRIVER NAME": toUpper(d.driverName || ""),
    "PHONE NO": toUpper(d.driverPhoneNumber || ""),
    "HELPER NAME": toUpper(d.helperName || d.khallasiName || ""),
    "HELPER PHONE": toUpper(d.helperPhoneNumber || d.khallasiPhoneNumber || ""),
    ADDRESS: toUpper(d.driverAddress || ""),
    "AADHAAR NO": toUpper(d.driverAadhaarNumber || ""),
    "LICENSE NO": toUpper(d.driverLicenseNumber || ""),
    DOB: fmtDate(d.driverDOB),
    "LICENSE CATEGORY": toUpper(d.licenseCategory || ""),
    "LICENSE VALID": fmtDate(d.licenseValidUpto),
    "BUS NO": toUpper(d.busNumber || ""),
    "VEHICLE NO": toUpper(d.vehicleNumber || ""),
  });

  const formatStudentRow = (s) => ({
    "ADMISSION NO": toUpper(s.studentDetails?.admission_no || ""),
    "STUDENT NAME": toUpper(s.studentDetails?.name || ""),
    CLASS: toUpper(s.currentClass || ""),
    SECTION: toUpper(s.studentDetails?.currentSection || ""),
    "ROLL NO": toUpper(s.studentDetails?.rollNo || ""),
    "PHONE NO": toUpper(s.studentDetails?.phoneNo || ""),
    "BUS STOP": toUpper(s.transportFacility?.busStop || ""),
    "BUS NO": toUpper(s.transportFacility?.busNumber || ""),
    "VEHICLE NO": toUpper(s.transportFacility?.vehicleNo || ""),
    "TRANSPORT FEE": String(
      getValueByPath(s, "feeDetails.transportFee.amt") || "",
    ),
    "FATHER NAME": toUpper(s.parentDetails?.[0]?.name || ""),
    "FATHER PHONE": toUpper(s.parentDetails?.[0]?.phoneNo || ""),
  });

  const formatRouteRow = (r) => ({
    "ROUTE ID": toUpper(r.id || r.routeId || ""),
    "BUS NO": toUpper(r.busNo || ""),
    PREFERENCE: toUpper(r.preference || ""),
    STOPPAGE: toUpper(r.stoppage || ""),
  });

  const formatStoppageRow = (r) => ({
    "STOPPAGE ID": toUpper(r.id || r.stoppageId || ""),
    "STOPPAGE NAME": toUpper(r.stoppage || r.name || ""),
    "BUS NO": toUpper(r.busNo || ""),
    "TRANSPORT FEE": String(r.transportFee || ""),
  });

  const formatTimingRow = (r) => ({
    "TIMING ID": toUpper(r.id || r.timingId || ""),
    "BUS NO": toUpper(r.busNo || ""),
    STOPPAGE: toUpper(r.stoppage || ""),
    "PICKUP TIME": toUpper(r.pickupTime || ""),
    "DROP TIME": toUpper(r.dropTime || ""),
  });

  // ─────────────────────────────────────────────────────────────────────────
  //  GENERIC GROUP-WISE HELPER
  // ─────────────────────────────────────────────────────────────────────────
  const groupWise = (data, keyFn, filename, formatter) => {
    if (!data.length) {
      toast.error("No records to export");
      return;
    }
    const wb = XLSX.utils.book_new();
    const groups = {};
    data.forEach((r) => {
      const key = toUpper(keyFn(r) || "UNKNOWN");
      if (!groups[key]) groups[key] = [];
      groups[key].push(formatter(r));
    });
    Object.entries(groups).forEach(([key, rows]) => {
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(rows),
        key.slice(0, 31),
      );
    });
    writeWorkbook(wb, `${filename}_${today}.xlsx`);
  };

  // ─────────────────────────────────────────────────────────────────────────
  //  BUS REPORTS
  // ─────────────────────────────────────────────────────────────────────────
  const reportBusAll = () => {
    if (!busData.length) {
      toast.error("No bus data");
      return;
    }
    const wb = XLSX.utils.book_new();
    const rows = [...busData]
      .sort(
        (a, b) =>
          Number(a.generalInfo?.busNumber) - Number(b.generalInfo?.busNumber),
      )
      .map(formatBusRow);
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(rows),
      "ALL BUSES",
    );
    writeWorkbook(wb, `Transport_AllBuses_${today}.xlsx`);
  };

  const reportBusCCTV = () => {
    const wb = XLSX.utils.book_new();
    const yes = busData
      .filter(
        (b) => (b.generalInfo?.cctvInstalled || "").toLowerCase() === "yes",
      )
      .map(formatBusRow);
    const no = busData
      .filter(
        (b) => (b.generalInfo?.cctvInstalled || "").toLowerCase() !== "yes",
      )
      .map(formatBusRow);
    if (yes.length)
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(yes),
        "CCTV INSTALLED",
      );
    if (no.length)
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(no), "NO CCTV");
    if (!wb.SheetNames.length) {
      toast.error("No data");
      return;
    }
    writeWorkbook(wb, `Transport_CCTV_Status_${today}.xlsx`);
  };

  const reportBusGPRS = () => {
    const wb = XLSX.utils.book_new();
    const active = busData
      .filter(
        (b) => (b.generalInfo?.gprsStatus || "").toLowerCase() === "active",
      )
      .map(formatBusRow);
    const inactive = busData
      .filter(
        (b) => (b.generalInfo?.gprsStatus || "").toLowerCase() !== "active",
      )
      .map(formatBusRow);
    if (active.length)
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(active),
        "GPRS ACTIVE",
      );
    if (inactive.length)
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(inactive),
        "GPRS INACTIVE",
      );
    if (!wb.SheetNames.length) {
      toast.error("No data");
      return;
    }
    writeWorkbook(wb, `Transport_GPRS_Status_${today}.xlsx`);
  };

  const reportBusInsurance = () => {
    if (!busData.length) {
      toast.error("No bus data");
      return;
    }
    const wb = XLSX.utils.book_new();
    const rows = busData.map((b) => {
      const g = b.generalInfo || {};
      const ins = b.insurance || {};
      return {
        "BUS NO": toUpper(g.busNumber || ""),
        "VEHICLE NO": toUpper(g.vehicleNumber || ""),
        "INSURANCE COMPANY": toUpper(ins.insuranceCompany || ""),
        "POLICY NO": toUpper(ins.insurancePolicyNumber || ""),
        "INSURANCE AMOUNT": String(ins.insuranceAmount || ""),
        "INSURANCE FROM": fmtDate(ins.insuranceDate),
        "INSURANCE TO": fmtDate(ins.insuranceExpiryDate),
        "FITNESS FROM": fmtDate(g.fitenessDate),
        "FITNESS TO": fmtDate(g.fitnessRenewalDate),
        "TAX FROM": fmtDate(g.tasPaidDate),
        "TAX TO": fmtDate(g.tasPaidExpiryDate),
      };
    });
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(rows),
      "INSURANCE",
    );
    writeWorkbook(wb, `Transport_BusInsurance_${today}.xlsx`);
  };

  const reportLeftBuses = () => {
    if (!leftBusData.length) {
      toast.error("No left bus data");
      return;
    }
    const wb = XLSX.utils.book_new();
    const rows = leftBusData.map((b) => ({
      "BUS NO": toUpper(b.busNumber || b.generalInfo?.busNumber || ""),
      "VEHICLE NO": toUpper(
        b.vehicleNumber || b.generalInfo?.vehicleNumber || "",
      ),
      "OWNER NAME": toUpper(b.ownerName || b.generalInfo?.ownerName || ""),
      SEATS: String(b.numberOfSeats || b.generalInfo?.numberOfSeats || ""),
    }));
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(rows),
      "LEFT BUSES",
    );
    writeWorkbook(wb, `Transport_LeftBuses_${today}.xlsx`);
  };

  // ─────────────────────────────────────────────────────────────────────────
  //  DRIVER REPORTS
  // ─────────────────────────────────────────────────────────────────────────
  const reportDriverAll = () => {
    if (!driverData.length) {
      toast.error("No driver data");
      return;
    }
    const wb = XLSX.utils.book_new();
    const rows = [...driverData]
      .sort((a, b) => Number(a.busNumber) - Number(b.busNumber))
      .map(formatDriverRow);
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(rows),
      "ALL DRIVERS",
    );
    writeWorkbook(wb, `Transport_AllDrivers_${today}.xlsx`);
  };

  const reportDriverBusWise = () =>
    groupWise(
      driverData,
      (d) => `Bus ${d.busNumber}`,
      "Transport_Drivers_BusWise",
      formatDriverRow,
    );

  const reportDriverLicense = () => {
    if (!driverData.length) {
      toast.error("No driver data");
      return;
    }
    const wb = XLSX.utils.book_new();
    const rows = driverData.map((d) => ({
      "DRIVER ID": toUpper(d.driverId || ""),
      "DRIVER NAME": toUpper(d.driverName || ""),
      "LICENSE NO": toUpper(d.driverLicenseNumber || ""),
      "LICENSE CATEGORY": toUpper(d.licenseCategory || ""),
      "LICENSE VALID": fmtDate(d.licenseValidUpto),
      "BUS NO": toUpper(d.busNumber || ""),
    }));
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(rows),
      "LICENSE DETAILS",
    );
    writeWorkbook(wb, `Transport_DriverLicense_${today}.xlsx`);
  };

  const reportLeftDrivers = () => {
    if (!leftDriverData.length) {
      toast.error("No left driver data");
      return;
    }
    const wb = XLSX.utils.book_new();
    const rows = leftDriverData.map((d) => ({
      "DRIVER ID": toUpper(d.driverId || ""),
      "DRIVER NAME": toUpper(d.driverName || ""),
      "PHONE NO": toUpper(d.driverPhoneNumber || ""),
      "BUS NO": toUpper(d.busNumber || ""),
    }));
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(rows),
      "LEFT DRIVERS",
    );
    writeWorkbook(wb, `Transport_LeftDrivers_${today}.xlsx`);
  };

  // ─────────────────────────────────────────────────────────────────────────
  //  STUDENT TRANSPORT REPORTS
  // ─────────────────────────────────────────────────────────────────────────
  const reportStudentTransportAll = () => {
    if (!transportStudents.length) {
      toast.error("No transport students");
      return;
    }
    const wb = XLSX.utils.book_new();
    const rows = transportStudents.map(formatStudentRow);
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(rows),
      "TRANSPORT STUDENTS",
    );
    writeWorkbook(wb, `Transport_Students_All_${today}.xlsx`);
  };

  const reportStudentBusWise = () => {
    if (!transportStudents.length) {
      toast.error("No transport students");
      return;
    }
    const wb = XLSX.utils.book_new();
    const groups = {};
    transportStudents.forEach((s) => {
      const bus = toUpper(s.transportFacility?.busNumber || "UNKNOWN");
      if (!groups[bus]) groups[bus] = [];
      groups[bus].push(formatStudentRow(s));
    });
    Object.entries(groups)
      .sort(([a], [b]) => Number(a) - Number(b))
      .forEach(([bus, rows]) => {
        rows.sort((a, b) =>
          (a["STUDENT NAME"] || "").localeCompare(b["STUDENT NAME"] || ""),
        );
        XLSX.utils.book_append_sheet(
          wb,
          XLSX.utils.json_to_sheet(rows),
          `BUS ${bus}`.slice(0, 31),
        );
      });
    writeWorkbook(wb, `Transport_Students_BusWise_${today}.xlsx`);
  };

  const reportStudentBusStopWise = () =>
    groupWise(
      transportStudents,
      (s) => s.transportFacility?.busStop,
      "Transport_Students_StopWise",
      formatStudentRow,
    );

  const reportStudentClassWise = () => {
    if (!transportStudents.length) {
      toast.error("No transport students");
      return;
    }
    const wb = XLSX.utils.book_new();
    const groups = {};
    transportStudents.forEach((s) => {
      const cls = s.currentClass || "UNKNOWN";
      const sec = s.studentDetails?.currentSection || "A";
      if (!groups[cls]) groups[cls] = {};
      if (!groups[cls][sec]) groups[cls][sec] = [];
      groups[cls][sec].push(formatStudentRow(s));
    });
    Object.entries(groups).forEach(([cls, sections]) => {
      const combined = [];
      Object.keys(sections)
        .sort()
        .forEach((sec) => {
          combined.push({ SECTION: `SECTION ${sec}` });
          combined.push(...sections[sec]);
          combined.push({});
        });
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(combined),
        toUpper(cls).slice(0, 31),
      );
    });
    writeWorkbook(wb, `Transport_Students_ClassWise_${today}.xlsx`);
  };

  const reportWalkers = () => {
    if (!walkerStudents.length) {
      toast.error("No walker students");
      return;
    }
    const wb = XLSX.utils.book_new();
    const rows = walkerStudents.map((s) => ({
      "ADMISSION NO": toUpper(s.studentDetails?.admission_no || ""),
      "STUDENT NAME": toUpper(s.studentDetails?.name || ""),
      CLASS: toUpper(s.currentClass || ""),
      SECTION: toUpper(s.studentDetails?.currentSection || ""),
      "ROLL NO": toUpper(s.studentDetails?.rollNo || ""),
      "PHONE NO": toUpper(s.studentDetails?.phoneNo || ""),
    }));
    rows.sort((a, b) =>
      (a["STUDENT NAME"] || "").localeCompare(b["STUDENT NAME"] || ""),
    );
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), "WALKERS");
    writeWorkbook(wb, `Transport_WalkerStudents_${today}.xlsx`);
  };

  // ─────────────────────────────────────────────────────────────────────────
  //  ROUTE / STOPPAGE / TIMING REPORTS
  // ─────────────────────────────────────────────────────────────────────────
  const reportRoutesAll = () => {
    if (!routeData.length) {
      toast.error("No route data");
      return;
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(routeData.map(formatRouteRow)),
      "BUS ROUTES",
    );
    writeWorkbook(wb, `Transport_Routes_${today}.xlsx`);
  };

  const reportRoutesBusWise = () =>
    groupWise(
      routeData,
      (r) => `Bus ${r.busNo}`,
      "Transport_Routes_BusWise",
      formatRouteRow,
    );

  const reportStoppagesAll = () => {
    if (!stoppageData.length) {
      toast.error("No stoppage data");
      return;
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(stoppageData.map(formatStoppageRow)),
      "BUS STOPPAGES",
    );
    writeWorkbook(wb, `Transport_Stoppages_${today}.xlsx`);
  };

  const reportTimingsAll = () => {
    if (!timingData.length) {
      toast.error("No timing data");
      return;
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(timingData.map(formatTimingRow)),
      "BUS TIMINGS",
    );
    writeWorkbook(wb, `Transport_Timings_${today}.xlsx`);
  };

  const reportTimingsBusWise = () =>
    groupWise(
      timingData,
      (r) => `Bus ${r.busNo}`,
      "Transport_Timings_BusWise",
      formatTimingRow,
    );

  // ─────────────────────────────────────────────────────────────────────────
  //  ANALYTICS
  // ─────────────────────────────────────────────────────────────────────────
  const reportTransportSummary = () => {
    const wb = XLSX.utils.book_new();
    const summary = [
      {
        MODULE: "Active Buses",
        COUNT: busData.length,
        NOTE: `${snapshot.totalSeats} total seats`,
      },
      {
        MODULE: "Drivers",
        COUNT: driverData.length,
        NOTE: `${leftDriverData.length} left`,
      },
      {
        MODULE: "Transport Students",
        COUNT: transportStudents.length,
        NOTE: `${walkerStudents.length} walkers`,
      },
      { MODULE: "Bus Routes", COUNT: routeData.length, NOTE: "" },
      { MODULE: "Bus Stoppages", COUNT: stoppageData.length, NOTE: "" },
      { MODULE: "Bus Timings", COUNT: timingData.length, NOTE: "" },
      { MODULE: "Left Buses", COUNT: leftBusData.length, NOTE: "" },
      { MODULE: "Left Drivers", COUNT: leftDriverData.length, NOTE: "" },
    ];
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(summary),
      "Summary",
    );

    // Bus-wise student count
    const busGroups = {};
    transportStudents.forEach((s) => {
      const bus = s.transportFacility?.busNumber || "Unknown";
      busGroups[bus] = (busGroups[bus] || 0) + 1;
    });
    const busRows = Object.entries(busGroups)
      .sort(([a], [b]) => Number(a) - Number(b))
      .map(([bus, count]) => ({ "BUS NO": bus, "STUDENT COUNT": count }));
    if (busRows.length)
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(busRows),
        "Students Per Bus",
      );

    writeWorkbook(wb, `Transport_Summary_${today}.xlsx`);
  };

  const reportMasterCombined = () => {
    const wb = XLSX.utils.book_new();
    if (busData.length)
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(busData.map(formatBusRow)),
        "Active Buses",
      );
    if (driverData.length)
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(driverData.map(formatDriverRow)),
        "Drivers",
      );
    if (transportStudents.length)
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(transportStudents.map(formatStudentRow)),
        "Transport Students",
      );
    if (leftBusData.length)
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(
          leftBusData.map((b) => ({
            "BUS NO": toUpper(b.busNumber || ""),
            "VEHICLE NO": toUpper(b.vehicleNumber || ""),
          })),
        ),
        "Left Buses",
      );
    if (leftDriverData.length)
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(
          leftDriverData.map((d) => ({
            "DRIVER ID": toUpper(d.driverId || ""),
            NAME: toUpper(d.driverName || ""),
          })),
        ),
        "Left Drivers",
      );
    if (routeData.length)
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(routeData.map(formatRouteRow)),
        "Routes",
      );
    if (stoppageData.length)
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(stoppageData.map(formatStoppageRow)),
        "Stoppages",
      );
    if (!wb.SheetNames.length) {
      toast.error("No data available");
      return;
    }
    writeWorkbook(wb, `Transport_MasterReport_${today}.xlsx`);
  };

  const reportAll = () => {
    if (!filteredData.length) {
      toast.error("No records to export");
      return;
    }
    const formatter =
      activeDataset === "drivers"
        ? formatDriverRow
        : activeDataset === "students"
          ? formatStudentRow
          : activeDataset === "busatt"
            ? formatBusAttRow
            : activeDataset === "busattstat"
              ? formatBusAttStatRow
              : formatBusRow;
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(filteredData.map(formatter)),
      DATASETS[activeDataset].label.slice(0, 31),
    );
    writeWorkbook(
      wb,
      `Transport_${DATASETS[activeDataset].label.replace(/ /g, "_")}_${today}.xlsx`,
    );
  };

  // ─────────────────────────────────────────────────────────────────────────
  //  BUS ATTENDANCE ROW FORMATTERS
  // ─────────────────────────────────────────────────────────────────────────
  const formatBusAttRow = (s) => ({
    "ADMISSION NO": toUpper(s.admission_no || ""),
    "STUDENT NAME": toUpper(s.name || ""),
    CLASS: toUpper(s.class || ""),
    SECTION: toUpper(s.section || ""),
    "ROLL NO": toUpper(s.rollNo || ""),
    "BUS NO": toUpper(s.busNo || ""),
    "PICKUP STATUS": toUpper(s?.busPickup?.status || ""),
    "PICKUP CREATED BY": toUpper(s?.busPickup?.createdBy || ""),
    "DROP STATUS": toUpper(s?.busDrop?.status || ""),
    "DROP CREATED BY": toUpper(s?.busDrop?.createdBy || ""),
    DATE: today,
  });

  const formatBusAttStatRow = (b) => ({
    "BUS NO": toUpper(String(b.BusNo || "")),
    "PICKUP PRESENT": Number(b.PickupPresent || 0),
    "PICKUP ABSENT": Number(b.PickupAbsent || 0),
    "DROP PRESENT": Number(b.DropPresent || 0),
    "DROP ABSENT": Number(b.DropAbsent || 0),
    "PICKUP MARKED": toUpper(b.isPickupMarked || ""),
    "DROP MARKED": toUpper(b.isDropMarked || ""),
  });

  // ─────────────────────────────────────────────────────────────────────────
  //  BUS ATTENDANCE REPORTS
  // ─────────────────────────────────────────────────────────────────────────
  const reportBusAttAll = () => {
    if (!busAttData.length) {
      toast.error("No bus attendance data");
      return;
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(busAttData.map(formatBusAttRow)),
      "BUS ATTENDANCE",
    );
    writeWorkbook(wb, `Transport_BusAttendance_All_${today}.xlsx`);
  };

  const reportBusAttPickupPresent = () => {
    const list = busAttData.filter((s) => s?.busPickup?.status === "Present");
    if (!list.length) {
      toast.error("No pickup-present records");
      return;
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(list.map(formatBusAttRow)),
      "PICKUP PRESENT",
    );
    writeWorkbook(wb, `Transport_BusPickup_Present_${today}.xlsx`);
  };

  const reportBusAttPickupAbsent = () => {
    const list = busAttData.filter((s) => s?.busPickup?.status === "Absent");
    if (!list.length) {
      toast.error("No pickup-absent records");
      return;
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(list.map(formatBusAttRow)),
      "PICKUP ABSENT",
    );
    writeWorkbook(wb, `Transport_BusPickup_Absent_${today}.xlsx`);
  };

  const reportBusAttDropPresent = () => {
    const list = busAttData.filter((s) => s?.busDrop?.status === "Present");
    if (!list.length) {
      toast.error("No drop-present records");
      return;
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(list.map(formatBusAttRow)),
      "DROP PRESENT",
    );
    writeWorkbook(wb, `Transport_BusDrop_Present_${today}.xlsx`);
  };

  const reportBusAttDropAbsent = () => {
    const list = busAttData.filter((s) => s?.busDrop?.status === "Absent");
    if (!list.length) {
      toast.error("No drop-absent records");
      return;
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(list.map(formatBusAttRow)),
      "DROP ABSENT",
    );
    writeWorkbook(wb, `Transport_BusDrop_Absent_${today}.xlsx`);
  };

  const reportBusAttBusWise = () => {
    if (!busAttData.length) {
      toast.error("No bus attendance data");
      return;
    }
    const wb = XLSX.utils.book_new();
    const groups = {};
    busAttData.forEach((s) => {
      const bus = toUpper(s.busNo || "UNKNOWN");
      if (!groups[bus]) groups[bus] = [];
      groups[bus].push(formatBusAttRow(s));
    });
    Object.entries(groups)
      .sort(([a], [b]) => Number(a) - Number(b))
      .forEach(([bus, rows]) => {
        XLSX.utils.book_append_sheet(
          wb,
          XLSX.utils.json_to_sheet(rows),
          `BUS ${bus}`.slice(0, 31),
        );
      });
    writeWorkbook(wb, `Transport_BusAttendance_BusWise_${today}.xlsx`);
  };

  const reportBusAttClassWise = () => {
    if (!busAttData.length) {
      toast.error("No bus attendance data");
      return;
    }
    const wb = XLSX.utils.book_new();
    const groups = {};
    busAttData.forEach((s) => {
      const cls = toUpper(s.class || "UNKNOWN");
      if (!groups[cls]) groups[cls] = [];
      groups[cls].push(formatBusAttRow(s));
    });
    Object.entries(groups).forEach(([cls, rows]) => {
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(rows),
        cls.slice(0, 31),
      );
    });
    writeWorkbook(wb, `Transport_BusAttendance_ClassWise_${today}.xlsx`);
  };

  // Statistics reports
  const reportBusAttStatAll = () => {
    if (!busAttStatData.length) {
      toast.error("No bus attendance statistics");
      return;
    }
    const wb = XLSX.utils.book_new();
    const rows = [...busAttStatData]
      .sort((a, b) => Number(a.BusNo) - Number(b.BusNo))
      .map(formatBusAttStatRow);
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(rows),
      "BUS ATT STATISTICS",
    );
    writeWorkbook(wb, `Transport_BusAttStat_All_${today}.xlsx`);
  };

  const reportBusAttStatMarked = () => {
    const list = busAttStatData.filter(
      (b) => b.isPickupMarked === "Marked" && b.isDropMarked === "Marked",
    );
    if (!list.length) {
      toast.error("No fully-marked bus records");
      return;
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(list.map(formatBusAttStatRow)),
      "MARKED",
    );
    writeWorkbook(wb, `Transport_BusAttStat_Marked_${today}.xlsx`);
  };

  const reportBusAttStatNotMarked = () => {
    const list = busAttStatData.filter(
      (b) =>
        b.isPickupMarked === "Not Marked" && b.isDropMarked === "Not Marked",
    );
    if (!list.length) {
      toast.error("No unmarked bus records");
      return;
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(list.map(formatBusAttStatRow)),
      "NOT MARKED",
    );
    writeWorkbook(wb, `Transport_BusAttStat_NotMarked_${today}.xlsx`);
  };

  const reportBusAttStatPartial = () => {
    const list = busAttStatData.filter(
      (b) =>
        (b.isPickupMarked === "Marked" && b.isDropMarked === "Not Marked") ||
        (b.isPickupMarked === "Not Marked" && b.isDropMarked === "Marked"),
    );
    if (!list.length) {
      toast.error("No partially-marked records");
      return;
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(list.map(formatBusAttStatRow)),
      "PARTIAL",
    );
    writeWorkbook(wb, `Transport_BusAttStat_Partial_${today}.xlsx`);
  };

  const reportBusAttMasterCombined = () => {
    const wb = XLSX.utils.book_new();
    if (busAttData.length)
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(busAttData.map(formatBusAttRow)),
        "All Attendance",
      );
    if (busAttStatData.length)
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(busAttStatData.map(formatBusAttStatRow)),
        "Bus Statistics",
      );
    const pickup = busAttData.filter((s) => s?.busPickup?.status === "Present");
    if (pickup.length)
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(pickup.map(formatBusAttRow)),
        "Pickup Present",
      );
    const drop = busAttData.filter((s) => s?.busDrop?.status === "Present");
    if (drop.length)
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(drop.map(formatBusAttRow)),
        "Drop Present",
      );
    if (!wb.SheetNames.length) {
      toast.error("No data");
      return;
    }
    writeWorkbook(wb, `Transport_BusAtt_MasterReport_${today}.xlsx`);
  };

  // ─────────────────────────────────────────────────────────────────────────
  //  REPORT DEFINITIONS LIST
  // ─────────────────────────────────────────────────────────────────────────
  const reports = [
    // Buses
    { cat: "buses", label: "All Active Buses", action: reportBusAll },
    { cat: "buses", label: "CCTV Status", action: reportBusCCTV },
    { cat: "buses", label: "GPRS Status", action: reportBusGPRS },
    { cat: "buses", label: "Insurance & Fitness", action: reportBusInsurance },
    { cat: "buses", label: "Left Buses", action: reportLeftBuses },
    // Drivers
    { cat: "drivers", label: "All Drivers", action: reportDriverAll },
    { cat: "drivers", label: "Bus Wise", action: reportDriverBusWise },
    { cat: "drivers", label: "License Details", action: reportDriverLicense },
    { cat: "drivers", label: "Left Drivers", action: reportLeftDrivers },
    // Students
    {
      cat: "students",
      label: "All Transport Students",
      action: reportStudentTransportAll,
    },
    {
      cat: "students",
      label: "Bus Wise Students",
      action: reportStudentBusWise,
    },
    {
      cat: "students",
      label: "Bus Stop Wise",
      action: reportStudentBusStopWise,
    },
    {
      cat: "students",
      label: "Class / Section Wise",
      action: reportStudentClassWise,
    },
    { cat: "students", label: "Walker Students", action: reportWalkers },
    // Routes
    { cat: "routes", label: "All Bus Routes", action: reportRoutesAll },
    { cat: "routes", label: "Routes — Bus Wise", action: reportRoutesBusWise },
    { cat: "routes", label: "All Stoppages", action: reportStoppagesAll },
    { cat: "routes", label: "All Timings", action: reportTimingsAll },
    {
      cat: "routes",
      label: "Timings — Bus Wise",
      action: reportTimingsBusWise,
    },
    // Analytics
    {
      cat: "analytics",
      label: "Transport Summary",
      action: reportTransportSummary,
    },
    {
      cat: "analytics",
      label: "Master Combined Report",
      action: reportMasterCombined,
    },
    // Bus Attendance
    { cat: "busatt", label: "All Bus Attendance", action: reportBusAttAll },
    {
      cat: "busatt",
      label: "Pickup — Present",
      action: reportBusAttPickupPresent,
    },
    {
      cat: "busatt",
      label: "Pickup — Absent",
      action: reportBusAttPickupAbsent,
    },
    { cat: "busatt", label: "Drop — Present", action: reportBusAttDropPresent },
    { cat: "busatt", label: "Drop — Absent", action: reportBusAttDropAbsent },
    { cat: "busatt", label: "Bus No Wise", action: reportBusAttBusWise },
    { cat: "busatt", label: "Class Wise", action: reportBusAttClassWise },
    {
      cat: "busatt",
      label: "Statistics — All Buses",
      action: reportBusAttStatAll,
    },
    {
      cat: "busatt",
      label: "Statistics — Marked",
      action: reportBusAttStatMarked,
    },
    {
      cat: "busatt",
      label: "Statistics — Not Marked",
      action: reportBusAttStatNotMarked,
    },
    {
      cat: "busatt",
      label: "Statistics — Partial",
      action: reportBusAttStatPartial,
    },
    {
      cat: "busatt",
      label: "Att. Master Report",
      action: reportBusAttMasterCombined,
    },
  ];

  const visibleReports =
    activeCategory === "all"
      ? reports
      : reports.filter((r) => r.cat === activeCategory);

  // ─────────────────────────────────────────────────────────────────────────
  //  RENDER
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <>
      <div id="db-wrapper">
        <main id="page-content">
          <div className="container-fluid my-3 px-3">
            <div className="row g-2">
              {/* ══════════════ LEFT COLUMN ══════════════ */}
              <div className="col-lg-8">
                {/* ── HERO HEADER ── */}
                <div className="bg-white rounded-4 p-4 mb-2">
                  <div className="bg-light rounded-4 p-4 position-relative overflow-hidden">
                    <div className="d-flex align-items-center">
                      <p className="badge bg-white text-dark rounded-pill px-4 py-2 mb-0 fs-6 fw-medium">
                        <i className="bi bi-bus-front me-1"></i> Reports &amp;
                        Analytics
                      </p>
                      <div className="ms-4">
                        <h3 className="fw-bold report-title m-0 lh-sm text-dark">
                          Transport Insights Hub
                        </h3>
                        <p className="text-secondary fs-6 fw-medium mb-0 lh-sm">
                          Generate, filter &amp; download every transport report
                          — all in one place.
                        </p>
                      </div>
                    </div>

                    {/* DATASET SELECTOR */}
                    <div className="mt-4">
                      <div className="row g-1">
                        {Object.entries(DATASETS).map(([key, ds]) => {
                          const isActive = activeDataset === key;
                          const count = datasetMap[key]?.data?.length || 0;
                          return (
                            <div className="col-md-3 col-6" key={key}>
                              <div
                                onClick={() => setActiveDataset(key)}
                                className={`p-3 rounded-4 cursor-pointer position-relative ${
                                  isActive
                                    ? `bg-${ds.color} text-white`
                                    : `bg-${ds.color}-soft`
                                }`}
                              >
                                {isActive && (
                                  <span className="position-absolute top-0 end-0 m-2">
                                    <i className="bi bi-check-circle-fill text-white fs-5"></i>
                                  </span>
                                )}
                                <p
                                  className={`m-0 fw-normal fs-5 lh-sm ${isActive ? "text-white" : "text-dark"}`}
                                >
                                  {ds.label}
                                </p>
                                <h3
                                  className={`fw-bold m-0 lh-1 mt-1 ${isActive ? "text-white" : "text-dark"}`}
                                >
                                  {count.toLocaleString("en-IN")}
                                </h3>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>

                {/* ── FILTERS ── */}
                <div className="bg-white rounded-4 p-4 mb-2">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <p className="m-0 fw-medium lh-1 text-dark fs-5">
                      <i className="bi bi-funnel-fill me-2 text-secondary"></i>
                      Refine Results
                    </p>
                    <span
                      onClick={() => {
                        setSelectedBusNo("");
                        setFromDate("");
                        setToDate("");
                        setSearchTerm("");
                      }}
                      className="badge bg-danger-soft text-dark rounded-3 px-3 py-2 cursor-pointer fw-medium"
                    >
                      <i className="bi bi-arrow-counterclockwise me-1"></i>{" "}
                      Reset
                    </span>
                  </div>

                  {/* Search */}
                  <div className="bg-light rounded-4 mb-3">
                    <form
                      className="d-flex align-items-center"
                      onSubmit={(e) => e.preventDefault()}
                    >
                      <span className="position-absolute ps-3 search-icon">
                        <i className="bi bi-filter-circle-fill ms-1" />
                      </span>
                      <input
                        type="search"
                        className="form-control ps-7 rounded-4 border-0 bg-light"
                        placeholder="Search bus no, vehicle no, driver, student name..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </form>
                  </div>

                  {/* Bus No */}
                  <div className="bg-light rounded-4 py-3 px-4 mb-2">
                    <p className="m-0 fw-medium lh-1 text-dark mb-2">
                      Filter by Bus Number
                    </p>
                    <div className="bg-white rounded-3 px-3 py-1 d-flex align-items-center">
                      <i className="bi bi-bus-front-fill text-secondary me-2"></i>
                      <Select
                        className="flex-grow-1"
                        isClearable
                        placeholder="All Bus Numbers"
                        value={
                          selectedBusNo
                            ? {
                                value: selectedBusNo,
                                label: `Bus ${selectedBusNo}`,
                              }
                            : null
                        }
                        onChange={(opt) =>
                          setSelectedBusNo(opt ? opt.value : "")
                        }
                        options={busNoOptions}
                        styles={selectStyles}
                      />
                    </div>
                  </div>

                  {/* Date Range */}
                  <div className="bg-light rounded-4 py-3 px-4 mb-2">
                    <p className="m-0 fw-medium lh-1 text-dark mb-2">
                      Date Range
                    </p>
                    <div className="row g-2">
                      <div className="col-md-6">
                        <div className="bg-white rounded-3 px-3 py-2 d-flex align-items-center">
                          <i className="bi bi-calendar-event text-secondary me-2"></i>
                          <input
                            type="date"
                            className="form-control border-0 p-0 fs-6 bg-transparent"
                            value={fromDate}
                            onChange={(e) => setFromDate(e.target.value)}
                          />
                        </div>
                        <p className="m-0 text-secondary fs-6 mt-1 ms-2 lh-1">
                          From
                        </p>
                      </div>
                      <div className="col-md-6">
                        <div className="bg-white rounded-3 px-3 py-2 d-flex align-items-center">
                          <i className="bi bi-calendar-check text-secondary me-2"></i>
                          <input
                            type="date"
                            className="form-control border-0 p-0 fs-6 bg-transparent"
                            value={toDate}
                            onChange={(e) => setToDate(e.target.value)}
                          />
                        </div>
                        <p className="m-0 text-secondary fs-6 mt-1 ms-2 lh-1">
                          To
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ── LIVE SNAPSHOT ── */}
                <div className="bg-white rounded-4 p-4 mb-2">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <p className="m-0 fw-medium lh-1 text-dark fs-5">
                      <i className="bi bi-pie-chart-fill me-2 text-secondary"></i>
                      Live Snapshot
                    </p>
                    <span className="badge bg-success-soft text-dark rounded-3 px-4 py-2 fw-medium">
                      <i className="bi bi-broadcast me-1"></i> Real-time
                    </span>
                  </div>

                  <div className="row g-2">
                    {/* Donut — Transport vs Walker */}
                    <div className="col-md-5">
                      <div className="bg-danger-soft rounded-4 p-4 h-100 d-flex flex-column align-items-center justify-content-center position-relative">
                        <svg viewBox="0 0 100 100" className="w-75">
                          <circle
                            cx="50"
                            cy="50"
                            r="40"
                            fill="none"
                            stroke="#fff"
                            strokeWidth="12"
                          />
                          <circle
                            cx="50"
                            cy="50"
                            r="40"
                            fill="none"
                            stroke="#198754"
                            strokeWidth="12"
                            strokeDasharray={`${snapshot.totalTransport + snapshot.totalWalkers ? (snapshot.totalTransport / (snapshot.totalTransport + snapshot.totalWalkers)) * 251.2 : 0} 251.2`}
                            transform="rotate(-90 50 50)"
                          />
                          <circle
                            cx="50"
                            cy="50"
                            r="40"
                            fill="none"
                            stroke="#ffc107"
                            strokeWidth="12"
                            strokeDasharray={`${snapshot.totalTransport + snapshot.totalWalkers ? (snapshot.totalWalkers / (snapshot.totalTransport + snapshot.totalWalkers)) * 251.2 : 0} 251.2`}
                            strokeDashoffset={`${snapshot.totalTransport + snapshot.totalWalkers ? -(snapshot.totalTransport / (snapshot.totalTransport + snapshot.totalWalkers)) * 251.2 : 0}`}
                            transform="rotate(-90 50 50)"
                          />
                        </svg>
                        <div className="position-absolute text-center">
                          <p className="m-0 fs-6 text-secondary lh-1">
                            Students
                          </p>
                          <h2 className="m-0 fw-bold text-dark lh-1 mt-1">
                            {(
                              snapshot.totalTransport + snapshot.totalWalkers
                            ).toLocaleString("en-IN")}
                          </h2>
                        </div>
                      </div>
                    </div>

                    {/* Stats grid */}
                    <div className="col-md-7">
                      <div className="row g-1 h-100">
                        <div className="col-6">
                          <div className="bg-success-soft rounded-4 p-4 h-100">
                            <i className="bi bi-bus-front-fill text-dark fs-3 d-block lh-1"></i>
                            <p className="m-0 fs-6 text-secondary lh-1 mt-2">
                              Buses
                            </p>
                            <h4 className="m-0 fw-bold text-dark">
                              {snapshot.totalBuses.toLocaleString("en-IN")}
                            </h4>
                            <p className="m-0 fs-6 text-secondary mt-1 lh-1">
                              {snapshot.totalSeats} total seats
                            </p>
                          </div>
                        </div>
                        <div className="col-6">
                          <div className="bg-info-soft rounded-4 p-4 h-100">
                            <i className="bi bi-person-fill text-dark fs-3 d-block lh-1"></i>
                            <p className="m-0 fs-6 text-secondary lh-1 mt-2">
                              Drivers
                            </p>
                            <h4 className="m-0 fw-bold text-dark">
                              {snapshot.totalDrivers.toLocaleString("en-IN")}
                            </h4>
                            <p className="m-0 fs-6 text-secondary mt-1 lh-1">
                              {snapshot.leftBuses} buses left
                            </p>
                          </div>
                        </div>
                        <div className="col-6">
                          <div className="bg-warning-soft rounded-4 p-4 h-100">
                            <i className="bi bi-people-fill text-dark fs-3 d-block lh-1"></i>
                            <p className="m-0 fs-6 text-secondary lh-1 mt-2">
                              Bus Students
                            </p>
                            <h4 className="m-0 fw-bold text-dark">
                              {snapshot.totalTransport.toLocaleString("en-IN")}
                            </h4>
                            <p className="m-0 fs-6 text-secondary mt-1 lh-1">
                              availing transport
                            </p>
                          </div>
                        </div>
                        <div className="col-6">
                          <div className="bg-dark-soft rounded-4 p-4 h-100">
                            <i className="bi bi-person-walking text-dark fs-3 d-block lh-1"></i>
                            <p className="m-0 fs-6 text-secondary lh-1 mt-2">
                              Walkers
                            </p>
                            <h4 className="m-0 fw-bold text-dark">
                              {snapshot.totalWalkers.toLocaleString("en-IN")}
                            </h4>
                            <p className="m-0 fs-6 text-secondary mt-1 lh-1">
                              self-commuting
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Bus Attendance mini-stats */}
                  {(busAttData.length > 0 || busAttStatData.length > 0) && (
                    <div className="row g-2 mt-1">
                      <div className="col-6">
                        <div className="bg-success-soft rounded-4 p-3 text-center">
                          <i className="bi bi-arrow-up-circle-fill text-dark fs-4 d-block lh-1"></i>
                          <p className="m-0 fs-6 text-secondary lh-1 mt-1">
                            Pickup Present
                          </p>
                          <h4 className="m-0 fw-bold text-dark">
                            {snapshot.busPickupPresent.toLocaleString("en-IN")}
                          </h4>
                          <p className="m-0 fs-6 text-secondary lh-1">
                            {snapshot.busPickupAbsent} absent
                          </p>
                        </div>
                      </div>
                      <div className="col-6">
                        <div className="bg-warning-soft rounded-4 p-3 text-center">
                          <i className="bi bi-arrow-down-circle-fill text-dark fs-4 d-block lh-1"></i>
                          <p className="m-0 fs-6 text-secondary lh-1 mt-1">
                            Drop Present
                          </p>
                          <h4 className="m-0 fw-bold text-dark">
                            {snapshot.busDropPresent.toLocaleString("en-IN")}
                          </h4>
                          <p className="m-0 fs-6 text-secondary lh-1">
                            {snapshot.notMarkedBuses} buses not marked
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* ══════════════ RIGHT COLUMN ══════════════ */}
              <div className="col-lg-4">
                <div className="mb-3">
                  <div className="mt-2 ms-4">
                    <nav aria-label="breadcrumb">
                      <ol className="breadcrumb pe-3">
                        <li className="breadcrumb-item">
                          <a href="#">Reports &amp; Analytics</a>
                        </li>
                        <li
                          className="breadcrumb-item active"
                          aria-current="page"
                        >
                          Transport
                        </li>
                      </ol>
                    </nav>
                  </div>
                </div>

                <div className="p-4 bg-white rounded-4">
                  {/* Avatar + count */}
                  <div className="bg-danger-soft rounded-4 p-3 d-flex align-items-center">
                    <div className="avatar-group">
                      {[1, 2, 3, 4, 5].map((num) => (
                        <span className="avatar avatar-md" key={num}>
                          <img
                            alt={`avatar-${num}`}
                            src={`/assets/images/students/${num}.jpg`}
                            className="rounded-circle"
                          />
                        </span>
                      ))}
                    </div>
                    <h3 className="fw-bold m-0 text-center fs-4 ms-3">
                      {filteredData.length.toLocaleString("en-IN")}
                    </h3>
                  </div>

                  {/* Title row */}
                  <div className="d-flex justify-content-between align-items-center mt-3">
                    <div className="d-flex align-items-center">
                      {currentLoading ? (
                        <div
                          className="spinner-border text-dark data-loader-spinner"
                          role="status"
                        ></div>
                      ) : (
                        <img src="/assets/images/folder.svg" alt="" />
                      )}
                      <p className="my-0 fw-semibold lh-1 fs-5 text-dark mx-3">
                        {DATASETS[activeDataset]?.label}
                        <br />
                        <span className="text-secondary fs-6 fw-medium lh-sm">
                          {reports.length} Reports Available
                        </span>
                      </p>
                    </div>
                    <span
                      onClick={reportAll}
                      className="badge bg-pink rounded-3 px-4 py-2 me-1 mt-1 cursor-pointer"
                    >
                      Export
                    </span>
                  </div>

                  {/* Category tabs */}
                  <div className="mt-3 d-flex flex-wrap gap-1">
                    {REPORT_CATEGORIES.map((cat) => (
                      <div
                        key={cat.key}
                        className={`bg-dark-soft rounded-3 cursor-pointer px-3 py-1 fs-6 ${
                          activeCategory === cat.key
                            ? "bg-warning text-white border-warning"
                            : ""
                        }`}
                        onClick={() => setActiveCategory(cat.key)}
                      >
                        {cat.label}
                      </div>
                    ))}
                  </div>

                  {/* Report buttons */}
                  <div className="px-4 py-2 mt-3 bg-light rounded-4">
                    <li className="list-group-item px-0">
                      <a
                        className="d-flex align-items-center text-inherit mb-0 justify-content-between"
                        data-bs-toggle="collapse"
                        href="#transportReportsCollapse"
                        role="button"
                        aria-expanded="true"
                        aria-controls="transportReportsCollapse"
                      >
                        <p className="m-0 fw-medium lh-sm text-dark">
                          Available Reports ({visibleReports.length})
                        </p>
                        <span className="chevron-arrow ms-4">
                          <i className="bi bi-chevron-down fs-4" />
                        </span>
                      </a>
                      <div className="collapse" id="transportReportsCollapse">
                        <div className="py-3">
                          <div className="rounded-4 d-flex flex-wrap">
                            {visibleReports.map((r, i) => (
                              <span
                                key={i}
                                onClick={r.action}
                                className="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                              >
                                {r.label}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </li>
                  </div>

                  {/* Quick action cards — Summary */}
                  <div className="mt-2">
                    <div className="row g-1">
                      <div className="col-6">
                        <div
                          onClick={reportTransportSummary}
                          className="p-3 bg-success-soft rounded-4 cursor-pointer"
                        >
                          <p className="m-0 text-center fw-medium lh-sm text-dark">
                            Summary
                          </p>
                          <p className="text-center fs-5 m-0 lh-1">
                            All Modules
                          </p>
                        </div>
                      </div>
                      <div className="col-6">
                        <div
                          onClick={reportMasterCombined}
                          className="p-3 bg-info-soft rounded-4 cursor-pointer"
                        >
                          <p className="m-0 text-center fw-medium lh-sm text-dark">
                            Master Report
                          </p>
                          <p className="text-center fs-5 m-0 lh-1">
                            Combined File
                          </p>
                        </div>
                      </div>
                      <div className="col-12">
                        <div
                          onClick={reportStudentBusWise}
                          className="p-3 bg-warning-soft rounded-4 cursor-pointer"
                        >
                          <p className="m-0 text-center fw-medium lh-sm text-dark">
                            Students Bus Wise
                          </p>
                          <p className="text-center fs-5 m-0 lh-1">
                            One Sheet Per Bus
                          </p>
                        </div>
                      </div>
                      <div className="col-6">
                        <div
                          onClick={reportBusAll}
                          className="p-3 bg-success-soft rounded-4 cursor-pointer"
                        >
                          <p className="m-0 text-center fw-medium lh-sm text-dark">
                            All Buses
                          </p>
                          <p className="text-center fs-5 m-0 lh-1">
                            Fleet Register
                          </p>
                        </div>
                      </div>
                      <div className="col-6">
                        <div
                          onClick={reportDriverAll}
                          className="p-3 bg-info-soft rounded-4 cursor-pointer"
                        >
                          <p className="m-0 text-center fw-medium lh-sm text-dark">
                            All Drivers
                          </p>
                          <p className="text-center fs-5 m-0 lh-1">
                            Driver Register
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
}

export default TransportReports;
